import EventTarget from "../util/EventTarget";

const BROWSER_PREFIXES = {
    webkitHidden: "webkitvisibilitychange",
    mozHidden: "mozvisibilitychange",
    msHidden: "msvisibilitychange",
    hidden: "visibilitychange"
} as const;

const FALLBACK_VISIBLE_EVENT = "focus";
const FALLBACK_INVISIBLE_EVENT = "blur";

function isDefined(something: unknown): boolean {
    return typeof something !== "undefined";
}

/**
 * Façade for registering callbacks to in and out-of-visibility events
 */
class Visibility extends EventTarget {

    private _hiddenAttribute: string | null = null;
    private _visibleChangeEvent: string | null = null;

    static EVENTS = {
        ON_VISIBLE: "visible",
        ON_INVISIBLE: "invisible"
    }

    constructor() {
        super();

        this._useBrowserPrefixedAttributes();
        this._contextifyCallbacks();
        this._subscribeToGlobalEvents();
    }

    /**
     * Bind methods to self for use as callbacks
     *
     * @private

     */
    _contextifyCallbacks() {
        this._dispatchEventListeners = this._dispatchEventListeners.bind(this);
        this._visibleEventListener = this._visibleEventListener.bind(this);
        this._invisibleEventListener = this._invisibleEventListener.bind(this);
    }

    /**
     * Registers global event listener for visibility change
     *
     * @private
     */
    _subscribeToGlobalEvents() {
        if (this._visibleChangeEvent) {
            document.addEventListener(this._visibleChangeEvent, this._dispatchEventListeners);

            return;
        }

        window.addEventListener(FALLBACK_VISIBLE_EVENT, this._visibleEventListener);
        window.addEventListener(FALLBACK_INVISIBLE_EVENT, this._invisibleEventListener);
    }

    /**
     * Tears down global event listener for visibility change
     *
     * @private
     */
    _unsubscribeFromGlobalEvents() {
        if (this._visibleChangeEvent) {
            document.removeEventListener(this._visibleChangeEvent, this._dispatchEventListeners);

            return;
        }

        window.removeEventListener(FALLBACK_VISIBLE_EVENT, this._visibleEventListener);
        window.removeEventListener(FALLBACK_INVISIBLE_EVENT, this._invisibleEventListener);
    }

    /**
     * Dispatches appropriate set of events, bound to document visibility change event
     *
     * @private
     */
    _dispatchEventListeners() {
        if (this.isVisible()) {
            this.dispatchEvent(Visibility.EVENTS.ON_VISIBLE);
        } else {
            this.dispatchEvent(Visibility.EVENTS.ON_INVISIBLE);
        }
    }

    /**
     * Determines whether or not focus/blur event is a visibility change
     */
    _isVisibilityChange({ view }: FocusEvent) {
        return view === window;
    }

    /**
     * Dispatches visible listeners if focus event is a visibility change
     *
     * @private
     */
    _visibleEventListener(event: FocusEvent) {
        if (this._isVisibilityChange(event)) {
            this.dispatchEvent(Visibility.EVENTS.ON_VISIBLE);
        }
    }

    /**
     * Dispatches invisible listeners if blur event is a visibility change
     *
     * @private
     */
    _invisibleEventListener(event: FocusEvent) {
        if (this._isVisibilityChange(event)) {
            this.dispatchEvent(Visibility.EVENTS.ON_INVISIBLE);
        }
    }

    /**
     * Selects correct version of `hidden` and `visibilitychange` for current browser
     *
     * @private
     */
    _useBrowserPrefixedAttributes() {
        Object.keys(BROWSER_PREFIXES).some((attr) => {
            if (isDefined(document[attr as keyof typeof document])) {
                this._hiddenAttribute = attr;
                this._visibleChangeEvent = BROWSER_PREFIXES[attr as keyof typeof BROWSER_PREFIXES];

                return true;
            }

            return false;
        });
    }

    /**
     * Destructor
     */
    destroy() {
        this._unsubscribeFromGlobalEvents();
    }

    /**
     * Determines whether or not the document is currently visible
     */
    isVisible(): boolean {
        // @ts-expect-error fix when not in Q4
        return document && document[this._hiddenAttribute] === false;
    }

}

export default Visibility;
